import React, { KeyboardEvent, useRef, useState } from 'react'
import style from './tokens.module.scss'
import { DeleteIcon, CurvedArrowIcon } from '../../icons'
import { WithOverrides } from '../../components/WithOverrides'
import { useAppAndBranchId, useQuery, useMutate, usePermissions } from '../../hooks'
import { api } from './api'
import { Modal } from '../../components'
import { useToast } from '../../../../../../helpers/commonFunction'
import NoData from "../../../../NoData";
import { ReactComponent as WarningIcon } from '../../../../../../assets/svg/warningIcon.svg'
import { Spin, Icon } from 'antd'
import { errorMessage } from '../../utils/error'

export function NLUTokens() {
  const inputRef = useRef<HTMLInputElement>(null)
  const [appId, branchId] = useAppAndBranchId()
  const { data: tokens, setData: setTokens, loading } = useQuery(() => api.getTokens(appId, branchId), branchId)
  const { mutate: addMutate, loading: addLoading } = useMutate(api.addToken)
  const { mutate: deleteMutate, loading: deleteLoading } = useMutate(api.deleteToken)
  const [modal, setModal] = useState({
    tokenId: "",
    visibility: false,
  })
  const [error, setError] = useState("")
  const deleteTokenName = tokens?.find(token => token.id === modal.tokenId)?.name
  const { ErrorToast} = useToast()

  const [canView, canCreate, canDelete] = usePermissions(['nlu.View', 'nlu.Create', 'nlu.Delete']) as boolean[]

	function validateToken(token: string) {
		if (!token.length) {
			setError('Enter token value');
			return false;
		}

		const isDuplicate = tokens?.find((el) => el.name === token) !== undefined;
		if (isDuplicate) {
			setError('Token already exists');
			return false;
		}

		return true;
	}

	async function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
		if (e.keyCode === 13 && !addLoading) {
			if (!validateToken(e.currentTarget.value.trim())) {
				return;
			}
			try {
				const data = await addMutate(appId, {
					name: e.currentTarget.value.trim(),
					branchId,
				});
				setTokens((prev) => {
					const tokens = Array.isArray(prev) ? [...prev] : [];
					tokens.push({ id: data.id, name: data.name });
					return tokens;
				});
				if (inputRef.current) {
					inputRef.current.value = '';
				}
			} catch (err) {
				ErrorToast(errorMessage("create", "token", err.code));
			}
		}
	}

  return (canView ?
    <WithOverrides>
      <div className={style.tokens}>
        <div className={style.header}>
          <h1 style={{ marginBottom: !canCreate ? 8 : 0 }}>Tokens</h1>
          {canCreate && <p>Create a new token</p> }
          <hr />
        </div>
        <div className={style.main}>
          <div className={style.tokensList}>
            {canCreate &&
              <div className={style.inputWrapper} style={{ marginBottom: error ? 16 : 0 }}>
                <input onChange={() => setError("")} ref={inputRef} onKeyDown={handleKeyDown} className={style.input} placeholder="Enter token here" />
                <CurvedArrowIcon width="15" height="15" stroke="#D9D9D9" strokeWidth="1.5" />
                {error && <span className={style.error}>{error}</span>}
              </div>
            }
            {loading && (
              <Spin indicator={<Icon type="loading" style={{ 
                fontSize: 24,
                position: "fixed",
                top: "50%",
                left: "50%",
                color: '#15808C'
              }} spin />} />
            )}
            {!loading && tokens?.map(token => {
              return (
                <div className={style.token} key={token.name}>
                  <p>{token.name}</p>
                  {canDelete && <div className={style.line} />}
                  {canDelete &&
                    <button onClick={() => setModal({ visibility: true, tokenId: token.id })}>
                      <DeleteIcon width="12" height="13" stroke="#292D32" />
                    </button>
                  }
                  <Modal 
                    styles={{
                      '--ok-btn-bg': '#DE0000',
                      '--ok-btn-filter': 'drop-shadow(0px 8px 13px rgba(211, 0, 73, 0.18))',
                    }}
                    okText="Delete"
                    loading={deleteLoading}
                    onOk={async () => {
                      try {
                        await deleteMutate(appId, branchId, modal.tokenId)
                        setTokens(prev => {
                          const tokens = Array.isArray(prev) ? [...prev] : []
                          return tokens.filter(token => token.id !== modal.tokenId)
                        })
                        setModal({ tokenId: "", visibility: false })
                      } catch(err) {
                        ErrorToast(errorMessage("delete", "token", err.code))
                      }
                    }}
                    onCancel={() => setModal({ tokenId: "", visibility: false })}
                    isOpen={modal.visibility && modal.tokenId === token.id}
                    title="Remove Chosen Token"
                  >
                    <div style={{
                      padding: '18px 14px',
                      color: '#393939',
                      fontSize: 15,
                      fontWeight: 500,
                    }}>
                      <p>Do you really want to remove the chosen token?</p>
                      {deleteTokenName && <span className="modal-delete-entity">{deleteTokenName}</span>}
                    </div>
                  </Modal>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </WithOverrides> :
      <div className="viewWrapper">
      <NoData
        svg={WarningIcon}
        header='Not Authorized'
        content='Please request owner of this organization to provide access'
        noButton={true}
      />
    </div>
  )
}
